import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import utc from 'dayjs/plugin/utc';

import { SortOrder } from '@page/core/types';
import { DAY_IN_MS } from '@page/core/constants';

// Enable plugins for dayjs
dayjs.extend(relativeTime);
dayjs.extend(customParseFormat);
dayjs.extend(utc);

/** IANA time zone for the browser — used when relative date filters need the lawyer's local day boundaries. */
export const getBrowserTimeZone = () => Intl.DateTimeFormat().resolvedOptions().timeZone;

/**
 * Extracts the date portion from an ISO string by removing the time component.
 *
 * @param isoString - The ISO date-time string (e.g., "2023-05-15T14:30:00Z")
 * @returns The date portion only (e.g., "2023-05-15")
 */
export const getDatePartOnlyFromISOString = (isoString: string) => {
  return isoString.split('T')[0];
};

interface ParseDateOnlyOptions {
  /** Custom format string for parsing. Supports dayjs format tokens (e.g. 'dd/MM/yyyy'). */
  fromFormat?: string;
  /**
   * If true, return `null` when the input can't be parsed instead of falling back to the current date.
   */
  returnNullOnInvalid?: boolean;
}

/**
 * Parses a date string and returns a Date object representing the date at midnight in the local timezone.
 *
 * This function handles various date formats and ensures the calendar date is preserved regardless of timezone.
 * For ISO strings with timezone information, it parses as UTC to extract the correct calendar date components,
 * then constructs a local date with those components. This prevents date shifts in UTC+ timezones.
 *
 * @param underterminedDate - The date string to parse. Supports multiple formats:
 *   - Custom format (when `options.fromFormat` is provided)
 *   - MM/DD/YYYY format (e.g., "05/15/2023")
 *   - ISO date-only format (e.g., "2023-05-15")
 *   - ISO datetime with timezone (e.g., "2023-05-15T14:30:00Z" or "2023-05-15T14:30:00+05:00")
 *   - Other formats (dayjs will attempt auto-parsing)
 *   - `null` or `undefined` are treated the same as an invalid input.
 * @param options - Optional parsing options. See {@link ParseDateOnlyOptions}.
 *
 * @returns A Date object representing the parsed date at midnight (00:00:00.000) in the local timezone.
 *   If the input cannot be parsed (including `null` / `undefined`), returns the current date at midnight —
 *   unless `options.returnNullOnInvalid` is true, in which case it returns `null`.
 *
 * @example
 * ```ts
 * // Parse ISO date string
 * parseDateOnly('2023-05-15'); // Returns Date for May 15, 2023 at midnight local time
 *
 * // Parse MM/DD/YYYY format
 * parseDateOnly('05/15/2023'); // Returns Date for May 15, 2023 at midnight local time
 *
 * // Parse with custom format
 * parseDateOnly('15/05/2023', { fromFormat: 'dd/MM/yyyy' });
 *
 * // Detect invalid input instead of silently falling back to today
 * parseDateOnly('N/A', { returnNullOnInvalid: true }); // null
 * ```
 */
export function parseDateOnly(
  underterminedDate: string | null | undefined,
  options: ParseDateOnlyOptions & { returnNullOnInvalid: true },
): Date | null;
export function parseDateOnly(underterminedDate: string | null | undefined, options?: ParseDateOnlyOptions): Date;
export function parseDateOnly(
  underterminedDate: string | null | undefined,
  options: ParseDateOnlyOptions = {},
): Date | null {
  const { fromFormat, returnNullOnInvalid = false } = options;
  const valueOnInvalid = returnNullOnInvalid ? null : dayjs().startOf('day').toDate();

  if (underterminedDate == null) {
    return valueOnInvalid;
  }

  // Convert to dayjs format if needed
  const dayjsFormat = fromFormat ? fromFormat.replace(/yyyy/g, 'YYYY').replace(/dd/g, 'DD') : undefined;

  // Regex patterns for common date formats
  const MM_DD_YYYY_PATTERN = /^\d{2}\/\d{2}\/\d{4}$/;
  const ISO_DATE_ONLY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
  const ISO_WITH_TIMEZONE_PATTERN = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{3})?(Z|[+-]\d{2}:\d{2})$/;

  let dayJSObject: dayjs.Dayjs | null = null;

  // Try parsing with the provided format first
  if (dayjsFormat) {
    dayJSObject = dayjs(underterminedDate, dayjsFormat);
  }

  // If no format provided or parsing failed, try common formats
  if (!dayJSObject?.isValid()) {
    if (MM_DD_YYYY_PATTERN.test(underterminedDate)) {
      dayJSObject = dayjs(underterminedDate, 'MM/DD/YYYY');
    } else if (ISO_DATE_ONLY_PATTERN.test(underterminedDate)) {
      dayJSObject = dayjs(underterminedDate, 'YYYY-MM-DD');
    } else if (ISO_WITH_TIMEZONE_PATTERN.test(underterminedDate)) {
      dayJSObject = dayjs.utc(underterminedDate);
    } else {
      // Try parsing without format (dayjs will attempt auto-parsing)
      dayJSObject = dayjs(underterminedDate);
    }
  }

  if (!dayJSObject?.isValid()) {
    return valueOnInvalid;
  }

  // Extract date components from the parsed date
  // For ISO strings with timezone, we parsed as UTC to get the correct calendar date
  // For other formats, we parsed in local timezone
  // We then create a local date with these components to preserve the calendar date
  const year = dayJSObject.year();
  const month = dayJSObject.month();
  const date = dayJSObject.date();

  // Create a date at midnight in the local timezone with the parsed date components
  // This ensures the date represents the correct calendar date regardless of timezone
  const localMidnightDayJSObject = dayjs()
    .year(year)
    .month(month)
    .date(date)
    .hour(0)
    .minute(0)
    .second(0)
    .millisecond(0);

  return localMidnightDayJSObject.toDate();
}

/**
 * Converts a Date object or date string to a Date object, optionally preserving time components.
 *
 * @param dateOrString - The date to convert. Can be a Date object or a date string.
 * @param preserveTime - If true, preserves the time components. If false, strips time and sets to midnight.
 * @returns A Date object with or without time components based on preserveTime flag.
 */
const getDateObjectFromDateOrString = (dateOrString: Date | string, preserveTime = false) => {
  if (preserveTime) {
    return typeof dateOrString === 'string' ? new Date(dateOrString) : dateOrString;
  }

  return typeof dateOrString === 'string' ? parseDateOnly(dateOrString) : dateOrString;
};

/**
 * Formats a date in US date format (MM/DD/YYYY or M/D/YYYY).
 *
 * @param dateOrString - The date to format. Can be a Date object or a date string.
 * @param full - If true, uses zero-padded format (MM/DD/YYYY). If false, uses short format (M/D/YYYY).
 * @returns Formatted date string in US format.
 */
export const formatUSDate = (dateOrString: Date | string, full = false) => {
  const dateObject = getDateObjectFromDateOrString(dateOrString);

  return dayjs(dateObject).format(full ? 'MM/DD/YYYY' : 'M/D/YYYY');
};

/**
 * Formats a date in Canadian locale format (YYYY-MM-DD) for API requests.
 *
 * @param dateOrString - The date to format. Can be a Date object or a date string.
 * @returns Formatted date string in YYYY-MM-DD format.
 */
export const formatRequestDate = (dateOrString: Date | string) => {
  const dateObject = getDateObjectFromDateOrString(dateOrString);

  return dateObject.toLocaleDateString('en-CA');
};

/**
 * Formats a date using US locale with medium date style, with optional custom formatting options.
 *
 * @param dateOrString - The date to format. Can be a Date object or a date string.
 * @param options - Optional Intl.DateTimeFormatOptions to customize the format.
 * @returns Formatted date string in US locale.
 */
export const formatDate = (dateOrString: Date | string, options?: Intl.DateTimeFormatOptions) => {
  const dateObject = getDateObjectFromDateOrString(dateOrString);

  return new Intl.DateTimeFormat('en-US', { dateStyle: 'medium', ...options }).format(dateObject);
};

/**
 * Formats a date and time using US locale with medium date and time styles, preserving time components.
 *
 * @param dateOrString - The date to format. Can be a Date object or a date string.
 * @param options - Optional Intl.DateTimeFormatOptions to customize the format.
 * @returns Formatted date and time string in US locale.
 */
export const formatDateTime = (dateOrString: Date | string, options?: Intl.DateTimeFormatOptions) => {
  const dateObject = getDateObjectFromDateOrString(dateOrString, true);

  return new Intl.DateTimeFormat('en-US', { dateStyle: 'medium', timeStyle: 'medium', ...options }).format(dateObject);
};

/**
 * Converts a Date object to an ISO string format.
 *
 * @param date - The date to convert to ISO string.
 * @param options - Options object with withTimezone flag.
 * @param options.withTimezone - If true, includes timezone offset. If false, returns UTC ISO string.
 * @returns ISO formatted date string.
 */
export const formatDateToISOString = (date: Date, options: { withTimezone?: boolean } = { withTimezone: false }) => {
  const dayjsDate = dayjs(date);

  if (options.withTimezone) return dayjsDate.format();

  return dayjsDate.toISOString();
};

/**
 * Groups objects into three date-based categories: previous 30 days, two months ago, and older.
 *
 * @param objects - Array of objects to group.
 * @param dateProperty - Property name containing the date string. Defaults to 'createdAt'.
 * @returns Object with three arrays: previous30DaysGroup, twoMonthsAgoGroup, and olderGroup.
 */
export const groupObjectsByDate = <T extends Record<string, unknown>>(objects: T[], dateProperty = 'createdAt') => {
  const currentDate = new Date();

  const thirtyDaysAgo = new Date();
  thirtyDaysAgo.setDate(currentDate.getDate() - 30);

  const twoMonthsAgo = new Date();
  twoMonthsAgo.setMonth(currentDate.getMonth() - 2);

  const previous30DaysGroup: T[] = [];
  const twoMonthsAgoGroup: T[] = [];
  const olderGroup: T[] = [];

  objects.forEach((object) => {
    if (typeof object[dateProperty] !== 'string') return;

    const createdAt = new Date(object[dateProperty] as string);

    if (createdAt >= thirtyDaysAgo) return previous30DaysGroup.push(object);

    if (createdAt >= twoMonthsAgo) return twoMonthsAgoGroup.push(object);

    olderGroup.push(object);
  });

  return {
    previous30DaysGroup,
    twoMonthsAgoGroup,
    olderGroup,
  };
};

/**
 * Compares two dates and returns a sort comparison value.
 *
 * @param dateA - First date to compare. Can be a Date object or a date string.
 * @param dateB - Second date to compare. Can be a Date object or a date string.
 * @returns 1 if dateA is later than dateB, -1 otherwise.
 */
export const sortDate = (dateA: Date | string, dateB: Date | string) => {
  return new Date(dateA) > new Date(dateB) ? 1 : -1;
};

/**
 * Sorts an array of dates in ascending or descending order.
 *
 * @param dates - Array of dates to sort. Can be Date objects or date strings.
 * @param sortOrder - Sort order. 'desc' for descending (newest first), 'asc' for ascending (oldest first).
 * @returns Sorted array of dates.
 */
export const sortDates = <T extends Date | string>(dates: T[], sortOrder: SortOrder = 'desc') => {
  const sortedDates = dates.toSorted(sortDate);

  if (sortOrder === 'desc') return sortedDates.toReversed();

  return sortedDates;
};

type IsoDateKey<T> = {
  [K in keyof T]-?: NonNullable<T[K]> extends string ? K : never;
}[keyof T];

const readIsoDate = (value: unknown) => {
  if (typeof value !== 'string') return '';

  return value;
};

/**
 * Sorts records earliest first by an ISO `YYYY-MM-DD` field.
 * That format sorts correctly as a string. Records with no date go last and keep their original order.
 */
export const sortByDate = <T extends object>(items: readonly T[], dateKey: IsoDateKey<T>): T[] =>
  items.toSorted((left, right) => {
    const leftDate = readIsoDate(left[dateKey]);
    const rightDate = readIsoDate(right[dateKey]);

    if (!leftDate && !rightDate) return 0;
    if (!leftDate) return 1;
    if (!rightDate) return -1;

    return leftDate.localeCompare(rightDate);
  });

/**
 * Returns a Date object representing the end of the day (23:59:59.999) for the given date.
 *
 * @param date - The date to get the end of day for.
 * @returns Date object set to the end of the day.
 */
export const getEndOfDay = (date: Date) => {
  const endOfDay = new Date(date);

  endOfDay.setHours(23, 59, 59, 999);

  return endOfDay;
};

/**
 * Returns the start and end dates of the year for the given date.
 *
 * @param year - A date within the year to get the boundaries for.
 * @returns Array with two Date objects: [startOfYear, endOfYear].
 */
export const getStartAndEndOfYear = (year: Date) => {
  const startOfYear = new Date(year);
  const endOfYear = new Date(year);

  startOfYear.setMonth(0, 1);
  startOfYear.setHours(0, 0, 0, 0);

  endOfYear.setMonth(11, 31);
  endOfYear.setHours(23, 59, 59, 999);

  return [startOfYear, endOfYear];
};

/**
 * Returns the start and end dates of the month for the given date.
 *
 * @param month - A date within the month to get the boundaries for.
 * @returns Array with two Date objects: [startOfMonth, endOfMonth].
 */
export const getStartAndEndOfMonth = (month: Date) => {
  const startOfMonth = new Date(month);
  const endOfMonth = new Date(month);

  startOfMonth.setDate(1);
  startOfMonth.setHours(0, 0, 0, 0);

  endOfMonth.setMonth(endOfMonth.getMonth() + 1, 0);
  endOfMonth.setHours(23, 59, 59, 999);

  return [startOfMonth, endOfMonth];
};

/**
 * Returns the full month name for a given month number (1-12).
 *
 * @param month - Month number (1-12) or string representation of month number.
 * @returns Full month name (e.g., "January") or empty string if invalid.
 */
export const getMonthText = (month: number | string) => {
  const monthAsNumber = Number(month);

  if (Number.isNaN(monthAsNumber) || monthAsNumber < 1 || monthAsNumber > 12) return '';

  const monthText = new Date(2021, monthAsNumber - 1, 1).toLocaleString('default', { month: 'long' });

  return monthText;
};

/**
 * Calculates the difference in days between two dates.
 *
 * @param dateA - The first date.
 * @param dateB - The second date.
 * @returns Number of days difference. Positive if dateA is later than dateB, negative otherwise.
 */
export const getDaysDiff = (dateA: Date, dateB: Date) => {
  const diff = dateA.getTime() - dateB.getTime();

  return diff / DAY_IN_MS;
};

/**
 * Returns a human-readable relative time string from now (e.g., "2 hours ago", "in 3 days").
 *
 * @param date - The date to compare against now. Can be a Date object or a date string.
 * @returns Relative time string.
 */
export const getRelativeTimeFromNow = (date: Date | string) => {
  return dayjs(date).fromNow();
};

/**
 * Checks if a Date object is valid.
 *
 * @param date - The Date object to validate.
 * @returns True if the date is valid, false otherwise.
 */
export const isDateObjectValid = (date: Date) => date.toString() !== 'Invalid Date';

/**
 * Checks if two dates fall on the same calendar day.
 *
 * @param dateA - First date to compare.
 * @param dateB - Second date to compare.
 * @returns True if both dates are on the same day, false otherwise.
 */
export const isSameDay = (dateA: Date, dateB: Date) => {
  return (
    dateA.getDate() === dateB.getDate() &&
    dateA.getMonth() === dateB.getMonth() &&
    dateA.getFullYear() === dateB.getFullYear()
  );
};

/**
 * Checks if a date is in the future compared to now.
 *
 * @param date - The date to check. Can be a Date object or a date string.
 * @returns True if the date is in the future, false otherwise.
 */
export const isDateInFuture = (date: Date | string) => {
  const dateObject = new Date(date);

  return dateObject > new Date();
};

/**
 * Checks if a date is today.
 *
 * @param date - The date to check. Can be a Date object or a date string.
 * @returns True if the date is today, false otherwise.
 */
export const isToday = (date: Date | string) => {
  return dayjs(date).isSame(dayjs(), 'day');
};

/**
 * Generates an array of Date objects representing each month from the start date to the current date.
 *
 * @param startDate - The starting date to begin generating months from.
 * @returns Array of Date objects in reverse chronological order (most recent first).
 */
export const generateMonthsFromDate = (startDate: Date): Date[] => {
  const dates: Date[] = [];
  const currentDate = new Date();

  const currentMonth = new Date(startDate);

  while (currentMonth <= currentDate) {
    dates.push(new Date(currentMonth));
    currentMonth.setMonth(currentMonth.getMonth() + 1);
  }

  return dates.reverse();
};

export type FormatMonthFormat = 'short' | 'long';

/**
 * Formats a date's month and year in either short or long format.
 *
 * @param date - The date to format.
 * @param format - Format style. 'short' returns "YYYY-MM", 'long' returns "MonthName YYYY".
 * @returns Formatted month and year string.
 */
export const formatMonth = (date: Date, format: FormatMonthFormat = 'long') => {
  const year = date.getFullYear();
  const month = date.getMonth();
  const monthName = date.toLocaleDateString('en-US', { month: 'long' });

  if (format === 'short') return `${year}-${String(month + 1).padStart(2, '0')}`;

  return `${monthName} ${year}`;
};

/**
 * Adds a specified amount of time to a date.
 *
 * @param date - The date to add time to.
 * @param amount - The amount to add (can be positive or negative).
 * @param unit - The unit of time (e.g., 'day', 'month', 'year', 'hour', 'minute').
 * @returns New Date object with the time added.
 */
export const addDateTime = (date: Date, amount: number, unit: dayjs.ManipulateType) => {
  return dayjs(date).add(amount, unit).toDate();
};

/** A calendar date, `YYYY-MM-DD`. Never a timestamp: these are dates on a letter, not instants. */
export type IsoDate = string;

const padIsoDatePart = (value: number) => String(value).padStart(2, '0');

/** Local midnight: `new Date('2018-01-09')` is parsed as UTC and shifts the day in western zones. */
export const parseIsoDate = (isoDate: IsoDate) => new Date(`${isoDate}T00:00:00`);

/** Built from local components — `toISOString()` shifts the day back in eastern zones. */
export const toIsoDate = (date: Date) =>
  `${date.getFullYear()}-${padIsoDatePart(date.getMonth() + 1)}-${padIsoDatePart(date.getDate())}`;

/**
 * Calendar arithmetic, not 24-hour arithmetic. Adding `days * 86_400_000` lands an hour short on
 * the spring-forward day and an hour long on the fall-back day, which is an off-by-one date
 * twice a year.
 */
export const addDays = (isoDate: IsoDate, days: number) => {
  const date = parseIsoDate(isoDate);

  date.setDate(date.getDate() + days);

  return toIsoDate(date);
};

export const addYears = (isoDate: IsoDate, years: number) => {
  const date = parseIsoDate(isoDate);

  date.setFullYear(date.getFullYear() + years);

  return toIsoDate(date);
};

/**
 * Subtracts a specified amount of time from a date.
 *
 * @param date - The date to subtract time from.
 * @param amount - The amount to subtract (can be positive or negative).
 * @param unit - The unit of time (e.g., 'day', 'month', 'year', 'hour', 'minute').
 * @returns New Date object with the time subtracted.
 */
export const subtractDateTime = (date: Date, amount: number, unit: dayjs.ManipulateType) => {
  return dayjs(date).subtract(amount, unit).toDate();
};

/**
 * Calculates the difference in years between the current date and the provided date.
 *
 * @param date - The date to compare against the current date. Can be a Date object or a date string.
 * @returns The number of years difference between now and the provided date. Returns a positive number if the date is in the past, negative if in the future.
 *
 * @example
 * ```ts
 * getYearsDiff(new Date('2020-01-01')); // Returns ~4 (if current year is 2024)
 * getYearsDiff('2020-01-01'); // Returns ~4 (if current year is 2024)
 * ```
 */
export const getYearsDiff = (date: Date | string) => {
  const dateObject = getDateObjectFromDateOrString(date);

  return dayjs().diff(dateObject, 'year');
};

/**
 * Formats a date as a compact US date string without separators (MMDDYYYY).
 *
 * @param dateOrString - The date to format. Can be a Date object or a date string.
 * @returns Formatted date string in MMDDYYYY format (e.g., "03062026").
 */
export const formatCompactUSDate = (dateOrString: Date | string) => {
  const dateObject = getDateObjectFromDateOrString(dateOrString);

  return dayjs(dateObject).format('MMDDYYYY');
};

/**
 * Formats a date range as "MMM D, YYYY – MMM D, YYYY" (e.g. "Jan 1, 2024 – Jan 31, 2024").
 *
 * @param startDate - The start date string or Date object.
 * @param endDate - The end date string or Date object.
 * @returns Formatted date range string.
 */
export const formatDateRange = (startDate: Date | string, endDate: Date | string): string => {
  const fmt = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  const start = typeof startDate === 'string' ? new Date(startDate) : startDate;
  const end = typeof endDate === 'string' ? new Date(endDate) : endDate;
  return `${fmt.format(start)} – ${fmt.format(end)}`;
};
